import React from "react";
import { Link } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";

import {
  ButtonGroupPrimary,
  ButtonGroupSecondary,
  ButtonGroupWrapper,
} from "@sainsburys-tech/luna-button";
import { Form, TextInputField } from "@sainsburys-tech/luna-form";
import { Card, SubmitButton, trackEvent } from "@jsainsburyplc/id-ui-common";

import missionConfig from "missionConfig";
import { getLoginLink, getInitiateAccountRecoveryLink } from "utils/urls";
import { EMAIL_REGEX } from "utils/validation";
import useCookies from "hooks/useCookies";
import { INVALID_EMAIL_MESSAGE } from "constants/messages";
import { categoryTypes, actionTypes } from "constants/tracking";
import { getForgottenPasswordLink } from "utils/urls";
import { isRecapchaEnabled } from "utils/env";

import { SubmitEmailFailure } from "./SubmitEmailFailure";
import RecaptchaNotice from "components/RecaptchaNotice";

const isRecaptchaEnabledEnv = isRecapchaEnabled();

export const FORGOTTEN_PASSWORD_FORM_ID = "forgotten-password-form";

const FORM_SCHEMA = Yup.object({
  email: Yup.string()
    .matches(EMAIL_REGEX, INVALID_EMAIL_MESSAGE)
    .required("Email is required"),
});

const INITAL_FORM_VALUES = {
  email: "",
};

const trackBackToLogIn = () => {
  trackEvent({
    category: categoryTypes.FORGOTTEN_PASSWORD,
    action: actionTypes.CLICK,
    label: "Back to login",
  });
};

const trackNoLongerHaveAccess = () => {
  trackEvent({
    category: categoryTypes.FORGOTTEN_PASSWORD,
    action: actionTypes.CLICK,
    label: "No longer have access",
  });
};

const { showBackToLoginButton } = missionConfig;

const ForgottenPasswordForm = ({ onSubmit }) => {
  const { email } = useCookies();
  return (
    <Formik
      initialValues={{ ...INITAL_FORM_VALUES, email: email ? email : "" }}
      validationSchema={FORM_SCHEMA}
      onSubmit={onSubmit}
      validateOnMount={true}
    >
      {({
        handleSubmit,
        handleChange,
        getFieldProps,
        values,
        errors,
        isSubmitting,
        isValid,
        touched,
        status = {},
      }) => (
        <Form
          id={FORGOTTEN_PASSWORD_FORM_ID}
          onSubmit={handleSubmit}
          className="o-form"
          action={getForgottenPasswordLink()}
          method="post"
        >
          {status.isError && <SubmitEmailFailure />}

          <Card>
            <TextInputField
              {...getFieldProps("email")}
              name="email"
              value={values.email}
              onChange={handleChange}
              label="Email address"
              type="email"
              maxLength={128}
              error={touched.email && errors?.email}
              required
            />
            {showBackToLoginButton && (
              <ButtonGroupWrapper className="ln-u-hard-top ln-u-margin-bottom">
                <ButtonGroupPrimary>
                  <SubmitButton
                    data-testid="submit-button"
                    disabled={!isValid || isSubmitting}
                    style={{ maxWidth: undefined }}
                    fullWidth={true}
                    isLoading={isSubmitting}
                    isLoadingLabel="Continue"
                    label="Continue"
                  />
                </ButtonGroupPrimary>
                <ButtonGroupSecondary>
                  <Link
                    to={getLoginLink()}
                    onClick={trackBackToLogIn}
                    className="ln-c-button ln-c-button--outlined ln-c-button--full"
                  >
                    Back to Log in
                  </Link>
                </ButtonGroupSecondary>
              </ButtonGroupWrapper>
            )}
            {!showBackToLoginButton && (
              <SubmitButton
                data-testid="submit-button"
                disabled={!isValid || isSubmitting}
                style={{ maxWidth: undefined }}
                fullWidth={true}
                isLoading={isSubmitting}
                isLoadingLabel="Continue"
                label="Continue"
              />
            )}
          </Card>

          {missionConfig.accountRecovery && (
            <Link
              data-testid="initiate-account-recovery"
              className="ln-c-link ln-c-link--bare ln-c-link--cta ln-c-link--alpha ln-u-margin-top*4"
              to={getInitiateAccountRecoveryLink()}
              onClick={trackNoLongerHaveAccess}
            >
              No longer have access to your email address?
            </Link>
          )}

          {isRecaptchaEnabledEnv && (
            <div className="ln-u-text-align-center">
              <RecaptchaNotice />
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default ForgottenPasswordForm;
